import InputAdornment from '@material-ui/core/InputAdornment';
import NoSsr from '@material-ui/core/NoSsr';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import classNames from 'classnames';
import React from 'react';
import Key from 'types/Key';

import * as formStyles from '../../styles/styles.module.scss';
import * as styles from './styles/styles.module.scss';

const StyledTextField = withStyles({
  root: {
    display: 'flex',
    width: '100%',
    borderRadius: '16px',
    fontFamily: 'Proxima Nova, Arial, sans-serif !important',
    '& .MuiFormLabel-root': {
      fontFamily: 'Proxima Nova, Arial, sans-serif !important',
      lineHeight: '8px'
    },
    '& .MuiFormLabel-root.MuiFormLabel-filled': {
      marginTop: '4px'
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px 16px',
      margin: '4px 0px',
      borderRadius: '8px',
      color: '#414141',
      fontFamily: 'Proxima Nova, Arial, sans-serif !important'
    },
    '& .MuiInputLabel-outlined': {
      color: '#b2aea7',
      zIndex: 0
    },
    '& .MuiOutlinedInput-multiline': {
      padding: '0px'
    },
    '&.verticalResize .MuiOutlinedInput-input': {
      resize: 'vertical'
    },
    '& input + fieldset': {
      borderRadius: '8px',
      color: '#767676',
      borderColor: '#e6e6e6'
    },
    '& input:valid:focus + fieldset': {
      borderLeftWidth: '2 !important',
      borderWidth: '2',
      borderColor: 'var(--color-border-interactive-active) !important',
      color: 'var(--color-border-interactive-active)'
    },
    '& input:focus + fieldset': {
      color: '#428A13',
      borderColor: '#428A13 !important'
    },
    '&. .MuiFormHelperText-contained': {
      margin: '4px 14px'
    },
    '& .MuiFormLabel-root.Mui-error': {
      color: 'var(--color-border-error) !important'
    },
    '& :hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--color-border-interactive-active) !important'
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px'
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      color: '#428A13 !important',
      borderColor: '#428A13 !important'
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#428A13',
      marginTop: '4px'
    },
    '& .MuiFormLabel-root.Mui-focused.Mui-error': {
      color: 'var(--color-text-error)'
    },
    '& .MuiFormHelperText-root': {
      fontFamily: 'Proxima Nova, Arial, sans-serif !important',
      color: 'var(--color-text-error) !important'
    },
    '& .MuiOutlinedInput-root.Mui-error': {
      minHeight: '47px !important' // for mobile safari
    },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      fontFamily: 'Proxima Nova, Arial, sans-serif !important',
      borderColor: 'var(--color-border-error) !important',
      borderWidth: '2px',
      color: 'var(--color-text-error) !important'
    }
  }
})(TextField);

function SsrStyledTextField({ label }: { label: React.ReactNode }) {
  return <div className={styles.ssrContainer}>{label}</div>;
}

type Props = {
  adornment?: React.ReactNode;
  autoFocus?: boolean;
  autocomplete?: string;
  changeHandler?(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void;
  className?: string;
  enterKeyHandler?(event: React.KeyboardEvent<HTMLDivElement> | React.FormEvent): void;
  errorMessage?: React.ReactNode;
  inputMode?: 'numeric';
  isDisabled?: boolean;
  isRequired?: boolean;
  isSuccess?: boolean;
  label?: React.ReactNode;
  maxLength?: number;
  multiline?: boolean;
  name?: string;
  onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
  onInput?(event: React.ChangeEvent<HTMLInputElement>): void;
  onKeyDown?(event: React.KeyboardEvent<HTMLDivElement>): void;
  onPaste?(event: React.ClipboardEvent<HTMLInputElement>): void;
  readOnly?: boolean;
  rows?: number;
  type?: string;
  value?: string;
  verticalResize?: boolean;
};

interface EndAdornmentProps {
  adornment?: React.ReactNode;
  isSuccess?: boolean;
}

function EndAdornment({ adornment, isSuccess }: EndAdornmentProps) {
  if (isSuccess) {
    return (
      <InputAdornment position="end">
        <CheckCircleIcon className={styles.successCheck} />
      </InputAdornment>
    );
  }

  if (adornment) {
    <InputAdornment position="end">{adornment}</InputAdornment>;
  }

  return null;
}

function OutlinedTextField({
  adornment,
  autocomplete,
  autoFocus,
  changeHandler,
  className,
  enterKeyHandler,
  errorMessage,
  inputMode,
  isDisabled,
  isRequired,
  isSuccess,
  label,
  maxLength,
  multiline,
  name,
  onBlur,
  onInput,
  onKeyDown,
  onPaste,
  readOnly,
  rows,
  type,
  value,
  verticalResize
}: Props) {
  // handle strings and FormattedMessage
  const hasError = errorMessage !== undefined && errorMessage !== '' && errorMessage !== false;

  return (
    <NoSsr fallback={<SsrStyledTextField label={label} />}>
      <StyledTextField
        autoComplete={autocomplete}
        autoFocus={autoFocus}
        className={classNames(
          className,
          styles.container,
          hasError && formStyles.inputError,
          multiline && styles.multiline,
          verticalResize && 'verticalResize'
        )}
        disabled={isDisabled}
        error={hasError}
        fullWidth
        helperText={hasError ? errorMessage : ''}
        InputProps={{
          endAdornment: <EndAdornment adornment={adornment} isSuccess={isSuccess} />,
          inputProps: {
            inputMode,
            maxLength
          },
          readOnly
        }}
        label={label}
        multiline={multiline}
        name={name}
        onBlur={onBlur}
        onChange={changeHandler}
        onInput={onInput}
        onKeyDown={onKeyDown}
        onKeyPress={e => {
          if (enterKeyHandler && e.key === Key.Enter) {
            enterKeyHandler(e);
          }
        }}
        onPaste={onPaste}
        required={isRequired}
        rows={rows}
        type={type}
        value={value}
        variant="outlined"
      />
    </NoSsr>
  );
}

export default OutlinedTextField;
